/**
 * =============================================================================
 * WlRecentPaymentsFeed
 *
 * Feed vertical do painel WL → Financeiro com os últimos recebimentos
 * (parcelas quitadas) de todos os tenants. Scroll infinito via
 * IntersectionObserver, consumindo `wl_list_recent_payments`.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-21 v1.0.0 - Versao inicial.
 * =============================================================================
 */
import { useEffect, useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useWlRecentPayments } from '@/hooks/wl/useWlRecentPayments';

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));

/**
 * Formata data no padrão "há X minutos/horas/dias".
 * Fallback simples que evita dependência extra de date-fns só para isso.
 */
function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = 60 * 1000;
  const hour = 60 * min;
  const day = 24 * hour;

  if (diff < hour) return `há ${Math.max(1, Math.floor(diff / min))} min`;
  if (diff < day) return `há ${Math.floor(diff / hour)} h`;
  const days = Math.floor(diff / day);
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** Feed "Últimos Recebimentos" cross-tenant (scroll infinito). */
export function WlRecentPaymentsFeed() {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useWlRecentPayments();

  // Ref para o sentinel do IntersectionObserver
  const sentinelRef = useRef<HTMLLIElement | null>(null);

  // Observador que dispara carregamento da próxima página quando o sentinel fica visível.
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: '80px', threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items = (data?.pages ?? []).flatMap((p) => p.items);

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          Últimos recebimentos
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex-1 min-h-0">
        <div className="h-full max-h-[880px] overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">Carregando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">Nenhum recebimento ainda</p>
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={it.id} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{it.tenant_name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {it.plan_name ?? it.descricao ?? '—'}
                      {it.numero_parcelas > 1 && (
                        <span className="ml-1">
                          · {it.numero_parcela}/{it.numero_parcelas}
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                      {fmtRelative(it.data_pagamento)}
                      {it.meio_pagamento ? ` · ${it.meio_pagamento}` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                      +{fmtBRL(it.valor_pago)}
                    </p>
                  </div>
                </li>
              ))}
              {/* Sentinel para scroll infinito */}
              <li ref={sentinelRef} className="h-4" />
              {isFetchingNextPage && (
                <li className="px-4 py-2 text-[11px] text-muted-foreground text-center">
                  Carregando mais…
                </li>
              )}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
